<style scoped lang="scss">
@import "@/assets/scss/global.scss";

.collection-title {
  font-size: 14px;
  font-weight: 500;
  line-height: 28px;
  color: $glaze-blue;

  // on the same line as image container below.
  // image container has 8 margin because of box-shadow settings
  margin-left: 8px;

  text-transform: uppercase;
}

.swiper-slide {
  flex-shrink: 1;
  flex-grow: 0;
  position: relative;
}

.swiper-inner-wrapper {
  height: 290px;
}

.swiper-container {
  padding: 8px 0;
  margin-right: 48px;

  @media (max-width: 1132px) {
    margin-right: 48px;
    margin-left: 48px;
  }

  @media (max-width: 960px) {
    margin-right: 0;
    margin-left: 0;
  }
}

.image-wrapper>div {
  flex: 1;
}

.image-container {
  text-align: center;
  margin: 0 8px;
}

.image-wrapper {
  position: relative;
  display: inline-flex;
  width: 100%;
}

.pre-animate-slide {
  opacity: 0;
  transform: translateY(15px);
  transition: opacity 565ms cubic-bezier(0.4, 0, 0.2, 1),
    transform 565ms cubic-bezier(0.4, 0, 0.2, 1);
}

.animate-slide {
  opacity: 1 !important;
  transform: translateY(0) !important;
}

.pre-pop-slide {
  transform: translateY(0px);
  transition: transform 165ms cubic-bezier(0.4, 0, 0.2, 1);
}

.pop-slide {
  transform: translateY(-2px) !important;
}

.view-all-button {
  margin: 0px;
  font-size: 12px;
  margin-left: 16px;
  color: $glaze-purple;
}

.swiper-top-bar {
  margin-bottom: 24px;

  @media (max-width: 1080px) {
    margin-left: 24px;
    margin-right: 24px;
  }
}

.slide-button {
  position: absolute;
  top: 50%;
  color: #6a6276;
  z-index: 2;
  height: 30px;
  width: 30px;

  .icon {
    font-size: 24px;
    padding-left: 2px;
  }

  &.right {
    right: 0px;
  }

  &.left {
    left: -48px;
  }

  @media (max-width: 1132px) {
    &.right {
      right: 0px;
    }

    &.left {
      left: 0px;
    }
  }

  @media (max-width: 960px) {
    display: none;
  }
}

.swiper-outer-wrapper {
  position: relative;
}

.swiper-slide-duplicate .pre-animate-slide {
  opacity: 1 !important;
  transform: translateY(0) !important;
}

.chevron-right {
  position: relative;

  img {
    position: absolute;
    top: -12px;
    right: -13px;
  }

  &.flipped {
    transform: rotate(180deg);
  }
}

.hover-label {
  text-align: left;
  position: absolute;
  bottom: -1px;
  width: 100%;
  height: 60px;
  background-color: $glaze-purple;
  visibility: hidden;

  .image-container.hover & {
    visibility: visible;
  }
}

.hover-label-text-block {
  color: white;
  margin: 12px;
  font-size: 11px;

  .artist-credit {
    text-transform: uppercase;
    font-weight: 500;
  }
}
</style>

<template>
  <div class="swiper-outer-wrapper">
    <v-btn small fab class="slide-button left pre-animate-slide" @click="slidePrev"
      :class="{ 'animate-slide': showLeftButton }" aria-label="prev-image">
      <div class="chevron-right flipped">
        <img src="@/assets/icons/chevron_right.svg" alt="">
      </div>
    </v-btn>
    <v-layout column ref="swiperContainer" class="swiper-inner-wrapper" v-scroll="onScroll">
      <v-flex class="swiper-top-bar">
        <v-layout row class="pre-animate-slide" :class="{ 'animate-slide': show }">
          <div class="collection-title" v-html='title'>
          </div>
          <v-btn small flat class="view-all-button" v-if="shouldShowViewAll()"
            @click="$router.push(`/browse?q=collection:${title}`)">view all</v-btn>
        </v-layout>
      </v-flex>
      <v-flex v-if="show">
        <swiper :options="swiperOption" ref="mySwiper">
          <swiper-slide v-for="(image, index) in images" :key="index">
            <v-hover>
              <div slot-scope="{ hover }" class="image-container" @click="$router.push(`/image/${image.image_id}`)"
                :class="{ 'elevation-2 hover': hover && enableHoverLabels }">
                <div class="image-wrapper pre-animate-slide" :class="{ 'animate-slide': showSlidesControl[index] }">
                  <progressive-image :src="imgUrl(image.image_id, false)" :placeholder="imgUrl(image.image_id, true)"
                    :max-height="232"></progressive-image>
                  <div class="hover-label" v-if="enableHoverLabels">
                    <v-layout column class="hover-label-text-block">
                      <v-flex class="artist-credit">
                        BY {{ image.display_name }}
                      </v-flex>
                      <v-flex>
                        Free with attribution
                      </v-flex>
                    </v-layout>
                  </div>
                </div>
              </div>
            </v-hover>
          </swiper-slide>
        </swiper>
      </v-flex>
    </v-layout>
    <v-btn small fab class="slide-button right pre-animate-slide" @click="slideNext" :class="{ 'animate-slide': show }"
      aria-label="next-image">
      <div class="chevron-right">
        <img src="@/assets/icons/chevron_right.svg" alt="">
      </div>
    </v-btn>
  </div>

</template>

<script>
import ProgressiveImage from "@/components/images/ProgressiveImage";

export default {
  props: {
    title: String,
    images: Array,
    index: Number,
    inViewport: {
      type: Boolean,
      default: false
    },
    showSignal: {
      type: Boolean,
      default: false
    },
    inShowQueue: {
      type: Boolean,
      default: false
    },
    enableHoverLabels: {
      type: Boolean,
      default: false
    },
    showViewAll: {
      type: Boolean,
      default: true
    }
  },
  components: {
    ProgressiveImage
  },
  mounted: function () {
    this.showSlidesControl.splice(this.images.length * 2);
    window.addEventListener("beforeunload", this.unloadAnimations);
  },
  data() {
    return {
      showLeftButton: false,
      shouldShow: false,
      hide: false,
      staggerIndex: 1,
      showSlidesControl: [],
      distanceFromTop: 0,
      swiperOption: {
        slidesPerView: "auto",
        watchSlidesVisibility: true,
        loop: false,
        preloadImages: false,
        lazy: true
      }
    };
  },
  computed: {
    swipeDistance() {
      return this.index % 2 == 0 ? 4 : 3;
    },
    container() {
      return this.$refs.swiperContainer;
    },
    show() {
      return (
        (this.shouldShow || this.showSignal) && !this.inShowQueue && !this.hide
      );
    },
    swiper() {
      return this.$refs.mySwiper.swiper;
    }
  },
  watch: {
    show: function (newVal, old) {
      if (old == false && newVal == true) {
        for (let i = 0; i < this.images.length; i++) {
          this.showSlideWithTimeout(i);
        }
      }
    }
  },
  methods: {
    shouldShowViewAll() {
      if (this.title == 'RELATED' || this.title.startsWith('MORE')) {
        return false;
      }
      return true;
    },
    imgUrl(imageid, isPlaceholder) {
      let baseUrl = `${process.env.VUE_APP_IMAGES_ROOT}${imageid}/thumbs/`;
      if (!isPlaceholder) {
        // this.$webp
        if (!this.isHdDisplay()) {
          return `${baseUrl}232.png`;
        }
        return `${baseUrl}464.png`;
      }

      return `${baseUrl}232c.png`;
    },
    isHdDisplay() {
      if (window.matchMedia) {
        var mq = window.matchMedia(
          "only screen and (min--moz-device-pixel-ratio: 1.3), only screen and (-o-min-device-pixel-ratio: 2.6/2), only screen and (-webkit-min-device-pixel-ratio: 1.3), only screen  and (min-device-pixel-ratio: 1.3), only screen and (min-resolution: 1.3dppx)"
        );
        return (mq && mq.matches) || window.devicePixelRatio > 1;
      }
    },
    slideNext() {
      if (!this.showLeftButton) {
        this.showLeftButton = true;
      }
      let slideIndex = this.swiper.realIndex + this.swipeDistance;
      this.swiper.slideToLoop(slideIndex, 600);
    },
    slidePrev() {
      let slideIndex = this.swiper.realIndex - this.swipeDistance;
      this.swiper.slideToLoop(slideIndex, 600);
    },
    showSlideWithTimeout(index) {
      let delay = 50 * this.staggerIndex;
      setTimeout(() => {
        // requestAnimationFrame(() => {
        this.$set(this.showSlidesControl, index, true);
        // });
      }, delay);
      this.staggerIndex = this.staggerIndex + 1;
    },
    isInViewport: function () {
      let bounding = this.container.getBoundingClientRect();
      let windowHeight = window.innerHeight;
      let topViewThreshold = 100;
      let bottomViewThreshold = windowHeight - 100;

      return (
        bounding.bottom > topViewThreshold && bounding.top < bottomViewThreshold
      );
    },
    async unloadAnimations() {
      this.hide = true;
      this.showLeftButton = false;
      for (let i = 0; i < this.showSlidesControl.length; i++) {
        this.$set(this.showSlidesControl, i, false);
      }
      await this.sleep(500);
    },
    sleep(ms) {
      return new Promise(resolve => setTimeout(resolve, ms));
    },
    // eslint-disable-next-line
    onScroll(e) {
      if (this.isInViewport()) {
        this.shouldShow = true;
      }
    },
    onViewAll: function () {
      this.$store.dispatch("search", {
        query: "collection:".concat(this.title),
      })
      this.$router.push("/browse")
    }
  }
};
</script>
